import React, { useLayoutEffect, useRef } from 'react';
import { RotateCcw } from 'lucide-react';
import { useI18n } from '../../../i18n/I18nContext';
import {
  MESSAGE_MAX, PLACEHOLDERS, PROMO_REDEEMABLE, SMS_PART, insertToken,
} from '../reminderUi';
import './Composer.css';

/**
 * The text of a message: a textarea, the placeholder chips ({name} {course} {link}, and {code} once
 * promo codes can be redeemed) that drop into the text at the caret, a character counter and the
 * note that an SMS over 160 Latin characters is billed as several parts (the counter turns amber
 * past 160 once SMS is one of the channels).
 *
 *   <MessageField id="msg" label="Xabar matni" value={text} onChange={setText} smsSelected error={err}
 *                 onReset={() => setText(defaultMessage)} />
 *
 * A chip click keeps the focus in the text (no blur on mousedown, so the keyboard of a phone stays
 * up) and puts the caret behind the inserted word. Before the textarea was ever focused the word
 * goes to the end of the text. `onReset` adds the "Standart matnga qaytarish" button to the label row.
 */
export default function MessageField({
  id,
  label,
  value,
  onChange,
  error,
  maxLength = MESSAGE_MAX,
  rows = 5,
  smsSelected = false,
  onReset,
  resetDisabled = false,
  required = true,
}) {
  const { t } = useI18n();
  const area = useRef(null);
  const focused = useRef(false);
  const caret = useRef(null);

  const insert = (key) => {
    const el = area.current;
    if (!el) return;
    const end = value.length;
    const from = focused.current ? el.selectionStart : end;
    const to = focused.current ? el.selectionEnd : end;
    const result = insertToken(value, `{${key}}`, from, to, maxLength);
    if (!result) return;
    caret.current = result.caret;
    onChange(result.value);
  };

  // The new text is in the DOM now: put the caret behind the word that was inserted.
  useLayoutEffect(() => {
    const el = area.current;
    if (caret.current === null || !el) return;
    const at = caret.current;
    caret.current = null;
    el.focus({ preventScroll: true });
    el.setSelectionRange(at, at);
  }, [value]);

  // {code} is only offered while a promo code can be redeemed (PROMO_REDEEMABLE)
  const chips = PLACEHOLDERS.filter((key) => key !== 'code' || PROMO_REDEEMABLE);
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const long = smsSelected && value.length > SMS_PART;

  return (
    <div className="ui-field rmc-message">
      <div className="rmc-message__label">
        <label className="ui-label" htmlFor={id}>
          {label}
          {required && <span className="ui-req" aria-hidden="true">*</span>}
        </label>
        {onReset && (
          <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm" onClick={onReset} disabled={resetDisabled}>
            <RotateCcw aria-hidden="true" />
            {t('reminders.message.reset')}
          </button>
        )}
      </div>

      <textarea
        id={id}
        ref={area}
        className="rmc-textarea"
        rows={rows}
        maxLength={maxLength}
        value={value}
        placeholder={t('reminders.message.placeholder')}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={`${hintId}${error ? ` ${errorId}` : ''}`}
        onFocus={() => { focused.current = true; }}
        onChange={(e) => onChange(e.target.value)}
      />

      <div className="rmc-message__tools">
        <div className="rmc-tokens" role="group" aria-label={t('reminders.message.insert')}>
          <span className="rmc-tokens__label" aria-hidden="true">{t('reminders.message.insert')}</span>
          {chips.map((key) => (
            <button
              key={key}
              type="button"
              className="ui-chip rmc-token"
              title={t(`reminders.message.ph.${key}`)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insert(key)}
            >
              {`{${key}}`}
            </button>
          ))}
        </div>
        <span className={`rmc-counter${long ? ' is-long' : ''}`}>{`${value.length} / ${maxLength}`}</span>
      </div>

      <p className="ui-hint" id={hintId}>{t('reminders.message.smsNote')}</p>
      {error && <p id={errorId} className="ui-error" role="alert">{error}</p>}
    </div>
  );
}
